import React, { JSX, ReactNode, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import RadioGroup, { RadioGroupOption } from '../newComponents/forms/RadioGroup/index.tsx'
import { Button } from '../newComponents/Button/Button.tsx'
import Card from '../newComponents/Card/Card.tsx'
import { Heading2, Heading3 } from '../newComponents/Headings'
import ProgressBar from '../newComponents/ProgressBar/index.tsx'

export enum ElementType {
	RATING = 'rating',
	HTML = 'html',
}

type ElementTypeWithoutHtml = Exclude<ElementType, ElementType.HTML>
type ElementTypeOnlyHtml = Extract<ElementType, ElementType.HTML>

export interface SurveyElement {
	name: string
	title: string
	type: ElementTypeWithoutHtml
	rateCount?: number
	rateValues?: number[]
	isRequired: boolean
	description?: string
}

export interface HtmlElement {
	type: ElementTypeOnlyHtml
	content: ReactNode | ReactNode[]
}

export interface Page {
	name?: string
	title?: string
	elements: (SurveyElement | HtmlElement)[]
}

export interface SurveyJson {
	pages: Page[]
	showQuestionNumbers: boolean
	pageNextText: string
	completeText: string
	firstPageIsStarted: boolean
	startSurveyText: string
	showPreviewBeforeComplete: boolean
	showProgressBar?: boolean
}

export interface SurveyAnswer {
	id: string
	value: string
}

interface SurveyBuilderProps {
	surveyJson: SurveyJson
	onSubmit: (answers: SurveyAnswer[]) => void
}

interface AnswersState {
	[elementName: string]: string
}

interface ErrorsState {
	[elementName: string]: boolean
}

const SurveyBuilder: React.FC<SurveyBuilderProps> = ({ surveyJson, onSubmit }) => {
	const [currentPageIndex, setCurrentPageIndex] = useState<number>(0)
	const [answers, setAnswers] = useState<AnswersState>({})
	const [errors, setErrors] = useState<ErrorsState>({})

	useEffect(() => {
		const url = new URL(window.location.toString())
		url.searchParams.set('page', currentPageIndex.toString())
		window.history.replaceState({}, '', url)
	}, [currentPageIndex])

	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search)
		const pageParam = urlParams.get('page')
		if (pageParam) {
			const pageIndex = parseInt(pageParam, 10)
			if (pageIndex >= 0 && pageIndex < surveyJson.pages.length) {
				setCurrentPageIndex(pageIndex)
			}
		}
	}, [surveyJson.pages.length])

	const currentPage = surveyJson.pages[currentPageIndex]
	const isFirstPage = currentPageIndex === 0
	const isFirstPageHtml = surveyJson.pages[0].elements.length === 1 && surveyJson.pages[0].elements[0].type === 'html'
	const isLastPage = currentPageIndex === surveyJson.pages.length - 1

	const handleAnswerChange = (elementName: string, value: string): void => {
		setAnswers((prev) => ({
			...prev,
			[elementName]: value,
		}))

		// Clear error when user provides an answer
		if (errors[elementName]) {
			setErrors((prev) => ({
				...prev,
				[elementName]: false,
			}))
		}
	}

	const validateCurrentPage = (): boolean => {
		const newErrors: ErrorsState = {}
		let hasErrors = false

		currentPage.elements.forEach((element: SurveyElement | HtmlElement) => {
			if (element.type !== 'html' && (element as SurveyElement).isRequired && !answers[(element as SurveyElement).name]) {
				newErrors[(element as SurveyElement).name] = true
				hasErrors = true
			}
		})

		setErrors(newErrors)
		return !hasErrors
	}

	const handleNext = (): void => {
		if (validateCurrentPage()) {
			if (isLastPage) {
				handleSubmit()
			} else {
				setCurrentPageIndex((prev) => prev + 1)
			}
		}
	}

	const handlePrevious = (): void => {
		if (!isFirstPage) {
			setCurrentPageIndex((prev) => prev - 1)
		}
	}

	const handleSubmit = (): void => {
		if (validateCurrentPage()) {
			const answersArray: SurveyAnswer[] = Object.entries(answers).map(([id, value]) => ({
				id,
				value,
			}))
			onSubmit(answersArray)
		}
	}

	const renderHtmlElement = (htmlContent: ReactNode | ReactNode[]): JSX.Element => {
		return (
			<div key={Math.random()}>
				{Array.isArray(htmlContent) ? htmlContent.map((content, index) => <div key={index}>{content}</div>) : htmlContent}
			</div>
		)
	}

	const renderRatingElement = (element: SurveyElement): JSX.Element => {
		const options: RadioGroupOption[] = element.rateValues
			? element.rateValues.map((val) => ({ value: val.toString(), label: val.toString() }))
			: Array.from({ length: element.rateCount || 5 }, (_, i) => ({
					value: (i + 1).toString(),
					label: (i + 1).toString(),
				}))

		return (
			<Card key={element.name} className="mb-6">
				<div className="mb-8">
					<Heading3>
						{surveyJson.showQuestionNumbers && (
							<span className="mr-2 text-blue-600">
								{surveyJson.pages
									.slice(0, currentPageIndex)
									.reduce((acc, page) => acc + page.elements.filter((el) => el.type !== 'html').length, 0) +
									currentPage.elements.filter((el) => el.type !== 'html').indexOf(element) +
									1}
								.
							</span>
						)}
						{element.title}
						{element.isRequired && <span className="text-red-500 ml-1">*</span>}
					</Heading3>
					{element.description && <p className="text-sm text-gray-600 mt-1">{element.description}</p>}
				</div>

				<RadioGroup
					name={element.name}
					options={options}
					selectedValue={answers[element.name] || ''}
					onChange={(value) => handleAnswerChange(element.name, String(value))}
					required={element.isRequired}
					className="justify-between px-3"
				/>

				{errors[element.name] && <p className="text-red-500 text-sm mt-2">This field is required</p>}
			</Card>
		)
	}

	const renderElement = (element: SurveyElement | HtmlElement): JSX.Element | null => {
		switch (element.type) {
			case ElementType.HTML:
				return renderHtmlElement((element as HtmlElement).content)
			case ElementType.RATING:
				return renderRatingElement(element)
			default:
				return null
		}
	}

	const getProgressPercentage = (): number => {
		if (isFirstPageHtml) {
			if (currentPageIndex === 0) {
				return 0
			}
			return (currentPageIndex / (surveyJson.pages.length - 1)) * 100
		}

		return ((currentPageIndex + 1) / surveyJson.pages.length) * 100
	}

	const getNextButtonText = (): string => {
		if (isFirstPage && isFirstPageHtml) return surveyJson.startSurveyText
		if (isLastPage) return surveyJson.completeText || 'Complete'
		return surveyJson.pageNextText || 'Next'
	}

	return (
		<div className="w-full flex flex-col items-center">
			{surveyJson.showProgressBar && (
				<div className="mb-8 w-full">
					<ProgressBar progress={getProgressPercentage()} />
					<p className="text-sm text-gray-600 mt-2">
						Page {currentPageIndex} of {isFirstPageHtml ? surveyJson.pages.length - 1 : surveyJson.pages.length}
					</p>
				</div>
			)}

			<div className="max-w-[680px] w-full">
				<div className="mb-8">
					{currentPage.title && (
						<Heading2 size="md" className="mb-4">
							{currentPage.title}
						</Heading2>
					)}

					<div className="space-y-6">{currentPage.elements.map(renderElement)}</div>
				</div>

				<div className="flex justify-between items-center pt-6 border-t border-gray-100 dark:border-white/[0.05]">
					{!(isFirstPage && isFirstPageHtml) && (
						<Button onClick={handlePrevious} disabled={isFirstPage} color="secondary">
							<ChevronLeft className="w-4 h-4" />
							<span>Previous</span>
						</Button>
					)}

					<Button onClick={handleNext} color="primary" className={`${isFirstPage && isFirstPageHtml ? 'ml-auto' : ''}`}>
						<span>{getNextButtonText()}</span>
						{!isLastPage && <ChevronRight className="w-4 h-4" />}
					</Button>
				</div>
			</div>
		</div>
	)
}

export default SurveyBuilder
